// StorageDatabasesDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored (AdSense)
// Updated to match InterviewGamePlanDetail.jsx UI + Template-first + Example (Facebook-like Home Feed)
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        /* ignore */
      }
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* ignore */
    }
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <h3 className="h6 text-uppercase text-muted mb-0">Sponsored</h3>
          <span className="badge rounded-pill text-bg-light border text-muted">Ads</span>
        </div>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small helpers (InterviewGamePlanDetail UI) ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <div className="d-flex align-items-start justify-content-between gap-2">
      <h2 className="h4 mb-2 fw-bold">{title}</h2>
    </div>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

const Say = ({ children }) => (
  <div className="alert alert-primary d-flex align-items-start gap-2 py-2 mb-3" role="note">
    <span className="badge text-bg-primary">Say</span>
    <div className="mb-0">{children}</div>
  </div>
);

const TagPill = ({ children }) => (
  <span className="badge rounded-pill text-bg-light border text-muted fw-semibold">{children}</span>
);

const Panel = ({ title, tag, children }) => (
  <div className="card border-0 shadow-sm mt-3">
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div className="fw-semibold">{title}</div>
        {tag ? <TagPill>{tag}</TagPill> : null}
      </div>
      {children}
    </div>
  </div>
);

const BulletList = ({ items }) => (
  <ul className="mb-0">
    {items.map((it, idx) => (
      <li key={idx} className={idx === items.length - 1 ? "" : "mb-3"}>
        <div>{it.main}</div>
        {it.detail ? <div className="medium text-muted mt-1">{it.detail}</div> : null}
      </li>
    ))}
  </ul>
);

const MonoBlock = ({ children }) => (
  <pre className="bg-light border rounded-3 p-3 mb-0" style={{ whiteSpace: "pre-wrap" }}>
    <code>{children}</code>
  </pre>
);

/* ================== TOC ================== */
const TOC = [
  { id: "sql-vs-nosql", label: "A) SQL vs NoSQL" },
  { id: "indexes", label: "B) Indexes" },
  { id: "transactions", label: "C) Transactions & isolation" },
  { id: "sharding", label: "D) Sharding" },
  { id: "replication", label: "E) Replication & quorum" },
  { id: "store-types", label: "F) Store types" },
  { id: "data-modeling", label: "G) Data modeling & amplification" },
  { id: "schema-evolution", label: "H) Schema evolution & compat" },
  { id: "case", label: "Mini case: Facebook-like Home Feed" },
  { id: "soundbites", label: "Quick sound bites" },
];

/* ================== page ================== */
export default function StorageDatabasesDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveId(entry.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  // Running example: Facebook-like Home Feed (keep stable across sections)
  const fb = useMemo(() => {
    const MAU = 500_000_000;
    const DAU = 200_000_000;
    const feedOpensPerUserPerDay = 8;
    const feedReadsPerDay = DAU * feedOpensPerUserPerDay; // 1.6B
    const avgFeedRps = feedReadsPerDay / 86400; // ~18.5k
    const peakMultiplier = 6;
    const peakFeedRps = avgFeedRps * peakMultiplier; // ~111k

    const postsPerDay = 20_000_000;
    const postBytes = 2_000; // rough metadata (text, ids, urls) excluding media bytes
    const postIngestGbPerDay = (postsPerDay * postBytes) / 1024 ** 3;

    const avgFollowers = 300; // heavy-tailed in reality
    const feedEntryBytes = 64; // postId, authorId, ts, score, flags, overhead
    const naiveFanoutEntriesPerDay = postsPerDay * avgFollowers;
    const naiveFeedEntriesGbPerDay = (naiveFanoutEntriesPerDay * feedEntryBytes) / 1024 ** 3;

    return {
      MAU,
      DAU,
      feedOpensPerUserPerDay,
      feedReadsPerDay,
      avgFeedRps,
      peakMultiplier,
      peakFeedRps,
      postsPerDay,
      postIngestGbPerDay,
      avgFollowers,
      naiveFeedEntriesGbPerDay,
      feedEntryBytes,
    };
  }, []);

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Storage &amp; databases</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(13,110,253,.08), rgba(108,117,125,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1 fw-bold">4) Storage &amp; databases — detailed guide</h1>
          <p className="text-muted mb-0" style={{ maxWidth: 900, margin: "0 auto" }}>
            Pick the right store, design the right keys and indexes, choose sane consistency, and evolve safely — with a
            running example: <strong>Facebook-like Home Feed</strong>.
          </p>
        </div>
      </header>

      {/* content: LEFT TOC + MAIN + RIGHT Sponsored */}
      <div className="row g-4">
        {/* LEFT: On this page */}
        <aside className="col-12 col-xl-2 d-print-none order-xl-1">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <h3 className="h6 text-uppercase text-muted mb-0">On this page</h3>
                  <TagPill>TOC</TagPill>
                </div>
                <nav className="nav flex-column small">
                  {TOC.map(({ id: tocId, label }) => (
                    <a
                      key={tocId}
                      href={`#${tocId}`}
                      className={`nav-link px-0 ${activeId === tocId ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === tocId ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">Tip: Use “Print” to save as PDF.</div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 order-xl-2">
          {/* A */}
          <Section id="sql-vs-nosql" title="A) SQL vs NoSQL — when to use which">
            <Say>“I choose the data store based on access patterns and guarantees, not popularity.”</Say>

            <Panel title="Checklist (how to choose)" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Start from access patterns:</strong> top reads/writes, latency SLOs, query shapes.
                      </>
                    ),
                    detail:
                      "If you can’t list the top 3 reads and writes, you’re not ready to pick a database.",
                  },
                  {
                    main: (
                      <>
                        <strong>Correctness needs:</strong> transactions, constraints, unique keys, referential integrity.
                      </>
                    ),
                    detail:
                      "Money, permissions, account state → strong correctness. Feeds/counters often tolerate bounded staleness.",
                  },
                  {
                    main: (
                      <>
                        <strong>Scale shape:</strong> read-heavy vs write-heavy, hotspot skew, partitionability.
                      </>
                    ),
                    detail:
                      "Horizontal scale is easiest when you can partition by a stable key (e.g., userId/viewerId).",
                  },
                  {
                    main: (
                      <>
                        <strong>Operational reality:</strong> backups, migrations, multi-region, observability, cost.
                      </>
                    ),
                    detail:
                      "Managed services reduce ops; but understand limits (indexes, query flexibility, transactional scope).",
                  },
                ]}
              />
            </Panel>

            <Panel title="SQL vs NoSQL — quick rule-of-thumb" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>SQL (relational):</strong> joins, constraints, ACID, ad-hoc queries.
                      </>
                    ),
                    detail:
                      "Great for accounts, payments, inventory, and workflows that need multi-row transactional correctness.",
                  },
                  {
                    main: (
                      <>
                        <strong>NoSQL (family):</strong> key-value, document, wide-column, time-series, graph, search.
                      </>
                    ),
                    detail:
                      "Great for narrow, high-scale access patterns, denormalized read models, sessions/caching, logs/telemetry.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — store choices" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Source of truth (correctness):</strong> Users, privacy settings, follows, and posts require strong correctness.
                      </>
                    ),
                    detail:
                      "These can live in SQL or a strongly consistent document store depending on scale and transactional needs.",
                  },
                  {
                    main: (
                      <>
                        <strong>Derived read model (scale):</strong> FeedEntry index is optimized for fast per-viewer scans (top-N).
                      </>
                    ),
                    detail:
                      "Use a KV/wide-column store partitioned by viewerId (or time-bucketed + viewerId).",
                  },
                  {
                    main: (
                      <>
                        <strong>Cache:</strong> Redis/Memcached for feed head pages and hot hydration (post summaries).
                      </>
                    ),
                    detail:
                      "Cache absorbs peak reads; correctness-sensitive reads (privacy) still require enforcement at read time or strict invalidation.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* B */}
          <Section id="indexes" title="B) Indexes — B-tree, hash, covering, composite">
            <Say>“Indexes buy read latency at the cost of write amplification. I keep OLTP indexes lean.”</Say>

            <Panel title="Index types & how to talk about them" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>B-tree:</strong> ordered, supports ranges/sorts.
                      </>
                    ),
                    detail:
                      "Best default for WHERE + ORDER BY patterns (time-based queries especially).",
                  },
                  {
                    main: (
                      <>
                        <strong>Hash:</strong> equality lookups only.
                      </>
                    ),
                    detail:
                      "Good for exact key lookups, but doesn’t help ranges/sorts.",
                  },
                  {
                    main: (
                      <>
                        <strong>Covering index:</strong> index contains all needed columns → index-only scan.
                      </>
                    ),
                    detail:
                      "Great for hot endpoints; but can bloat writes if you include too many columns.",
                  },
                  {
                    main: (
                      <>
                        <strong>Composite index:</strong> multi-column; <em>leftmost prefix</em> matters.
                      </>
                    ),
                    detail:
                      "Equality/high-selectivity columns first; range columns later.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — indexing examples" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Posts lookup:</strong> hydrate posts by <code>postId</code> in batch.
                      </>
                    ),
                    detail:
                      "Primary key / hash index on postId. Keep the row/doc small; put large media elsewhere.",
                  },
                  {
                    main: (
                      <>
                        <strong>FeedEntry scan:</strong> query “top-N for viewer”.
                      </>
                    ),
                    detail:
                      "Partition by viewerId; sort by (rankScore, createdAt) or time bucket + createdAt. This avoids global secondary indexes for the main read.",
                  },
                  {
                    main: (
                      <>
                        <strong>Follow edges:</strong> <code>followers(authorId)</code> and/or <code>following(viewerId)</code>.
                      </>
                    ),
                    detail:
                      "Two access patterns may require two tables/indexes (or denormalized edges) depending on fan-out logic.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* C */}
          <Section id="transactions" title="C) Transactions & isolation — ACID, MVCC, what must be strong">
            <Say>“I reserve strong transactions for correctness-critical flows; everything else can be async/derived.”</Say>

            <Panel title="Isolation levels (interview-ready)" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Read Committed:</strong> no dirty reads; non-repeatable reads possible.
                      </>
                    ),
                    detail:
                      "Common default; fine for many apps with careful update patterns.",
                  },
                  {
                    main: (
                      <>
                        <strong>Repeatable Read / Snapshot (MVCC):</strong> consistent snapshot; readers don’t block writers.
                      </>
                    ),
                    detail:
                      "Great for read-heavy workloads; watch long transactions causing bloat/compaction pressure.",
                  },
                  {
                    main: (
                      <>
                        <strong>Serializable:</strong> strongest; can abort more transactions under contention.
                      </>
                    ),
                    detail:
                      "Use for money, inventory decrements, unique constraints under high concurrency.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — where transactions matter" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Privacy/block rules:</strong> must apply immediately and correctly.
                      </>
                    ),
                    detail:
                      "Privacy changes may require cache invalidations and re-checks at read time to avoid leaking content.",
                  },
                  {
                    main: (
                      <>
                        <strong>Post creation:</strong> once acknowledged, the post must be durable.
                      </>
                    ),
                    detail:
                      "Write post to durable store, then emit PostCreated event to build feed/search indexes asynchronously.",
                  },
                  {
                    main: (
                      <>
                        <strong>FeedEntry building:</strong> derived/indexed; eventual consistency is acceptable (seconds).
                      </>
                    ),
                    detail:
                      "If fan-out pipeline lags, degrade gracefully (recency fallback) rather than blocking writes.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* D */}
          <Section id="sharding" title="D) Sharding — hash/range, consistent hashing, hot-key mitigation">
            <Say>“I shard by a stable key and plan for hotspots up front.”</Say>

            <Panel title="Sharding strategies" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Hash sharding:</strong> even spread; great for KV/doc stores.
                      </>
                    ),
                    detail:
                      "Downside: range queries become scatter/gather unless you add secondary structures.",
                  },
                  {
                    main: (
                      <>
                        <strong>Range sharding:</strong> good locality for time/id; risk hot ‘latest’ range.
                      </>
                    ),
                    detail:
                      "Use time buckets to avoid hot partitions and to simplify TTL/retention.",
                  },
                  {
                    main: (
                      <>
                        <strong>Consistent hashing + virtual nodes:</strong> smooth rebalancing.
                      </>
                    ),
                    detail:
                      "Common for caches and large KV partitions; reduces reshard pain.",
                  },
                  {
                    main: (
                      <>
                        <strong>Hot-key mitigation:</strong> salting, dedicated partitions, batching, queues.
                      </>
                    ),
                    detail:
                      "Celebrities, trending keys, and ‘global counters’ need special treatment.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — sharding plan" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>FeedStore:</strong> shard by <code>viewerId</code> (many virtual shards).
                      </>
                    ),
                    detail:
                      "This matches the read pattern (top-N for viewer) and distributes reads evenly.",
                  },
                  {
                    main: (
                      <>
                        <strong>Hot creators:</strong> celebrity posts are not fanned-out to every follower partition.
                      </>
                    ),
                    detail:
                      `Naive fan-out can be huge (≈ ${(fb.naiveFeedEntriesGbPerDay).toFixed(
                        0
                      )} GB/day at ~${fb.feedEntryBytes}B/entry with avg followers ~${fb.avgFollowers}). Use hybrid: fan-in merge for celebrities.`,
                  },
                  {
                    main: (
                      <>
                        <strong>Time buckets:</strong> bucket feed entries by day/hour to control partition size.
                      </>
                    ),
                    detail:
                      "Buckets make retention/cleanup easy and reduce compaction pressure in LSM/wide-column stores.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* In-article ad (optional) */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
            </div>
          )}

          {/* E */}
          <Section id="replication" title="E) Replication & quorum — sync/async, leader/follower, R/W quorum">
            <Say>“I’ll state what must be strongly consistent and where we tolerate bounded staleness.”</Say>

            <Panel title="Replication models" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Leader/follower (primary/replica):</strong> simple writes, great read scaling.
                      </>
                    ),
                    detail:
                      "Sync replication lowers RPO but raises latency; async replication is faster but can lag.",
                  },
                  {
                    main: (
                      <>
                        <strong>Quorum (Dynamo-style):</strong> write to N with W, read with R; <code>R + W &gt; N</code> gives quorum overlap.
                      </>
                    ),
                    detail:
                      "Use hinted handoff/read repair; define conflict resolution strategy.",
                  },
                  {
                    main: (
                      <>
                        <strong>Client semantics:</strong> read-your-writes, monotonic reads, session stickiness.
                      </>
                    ),
                    detail:
                      "Often implemented by routing a session to leaders or tagging reads for consistency when needed.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — consistency choices" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Strong where needed:</strong> privacy settings, blocks, and account security.
                      </>
                    ),
                    detail:
                      "Reads can be pinned to leader (or use strongly consistent reads) for these entities.",
                  },
                  {
                    main: (
                      <>
                        <strong>Eventual where acceptable:</strong> feed entries, counters, ranking signals.
                      </>
                    ),
                    detail:
                      "Followers may see posts within seconds; like counts may lag and reconcile later.",
                  },
                  {
                    main: (
                      <>
                        <strong>Read-your-writes:</strong> author should see their new post immediately.
                      </>
                    ),
                    detail:
                      "Write-through cache or leader reads for author; followers rely on async fan-out with bounded lag.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* F */}
          <Section id="store-types" title="F) Store types — strengths & trade-offs">
            <Say>“I’ll map store types to access patterns and call out what breaks at scale.”</Say>

            <Panel title="Store catalog" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Key-value:</strong> O(1) lookups, sessions, rate limiting, caching.
                      </>
                    ),
                    detail:
                      "Limited ad-hoc queries → design keys and GSIs carefully.",
                  },
                  {
                    main: (
                      <>
                        <strong>Wide-column:</strong> huge scale, time-ordered scans, tunable consistency.
                      </>
                    ),
                    detail:
                      "Schema equals access patterns; great for feed/time-series partitions.",
                  },
                  {
                    main: (
                      <>
                        <strong>Document:</strong> flexible JSON, nested data, secondary indexes.
                      </>
                    ),
                    detail:
                      "Govern schema growth; large documents increase write amplification.",
                  },
                  {
                    main: (
                      <>
                        <strong>Relational (OLTP):</strong> transactions, constraints, joins.
                      </>
                    ),
                    detail:
                      "Scale via partitioning, read replicas, caching; avoid hot joins on critical paths.",
                  },
                  {
                    main: (
                      <>
                        <strong>Search:</strong> full-text and facets; requires indexing pipeline.
                      </>
                    ),
                    detail:
                      "Typically eventual; treat as derived view with backfill/reindex plans.",
                  },
                  {
                    main: (
                      <>
                        <strong>OLAP/columnar:</strong> analytics at scale; not for OLTP.
                      </>
                    ),
                    detail:
                      "Push exploration/BI here to keep OLTP fast and cheap.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — mapping stores" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>PostStore:</strong> durable metadata/text (SQL/doc) + object store for media.
                      </>
                    ),
                    detail:
                      "Keep blobs out of OLTP rows/docs; serve media via CDN.",
                  },
                  {
                    main: (
                      <>
                        <strong>FeedStore:</strong> wide-column/KV for fast per-viewer scans.
                      </>
                    ),
                    detail:
                      "Supports top-N reads with predictable latency under high QPS.",
                  },
                  {
                    main: (
                      <>
                        <strong>Search index:</strong> posts/users search is derived and asynchronously updated.
                      </>
                    ),
                    detail:
                      "Define index lag SLO and fallbacks when indexing is behind.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* G */}
          <Section id="data-modeling" title="G) Data modeling — normalization vs denormalization; amplification">
            <Say>“I normalize the source-of-truth and denormalize read models where latency matters.”</Say>

            <Panel title="Core concepts" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Normalization:</strong> fewer duplicates, easier updates, correctness.
                      </>
                    ),
                    detail:
                      "But joins on hot paths raise p95; careful indexing and caching becomes mandatory.",
                  },
                  {
                    main: (
                      <>
                        <strong>Denormalization:</strong> fast reads, fewer joins.
                      </>
                    ),
                    detail:
                      "Costs: write amplification + propagation complexity (async updates, backfills).",
                  },
                  {
                    main: (
                      <>
                        <strong>Amplification:</strong> read amplification (many reads per request) and write amplification (many writes per logical write).
                      </>
                    ),
                    detail:
                      "Indexes, LSM compaction, and multiple projections increase write amplification.",
                  },
                  {
                    main: (
                      <>
                        <strong>Patterns:</strong> CQRS and event sourcing to build read models safely.
                      </>
                    ),
                    detail:
                      "Keep OLTP clean; build read-optimized views asynchronously; monitor lag and correctness.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — modeling approach" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Source-of-truth:</strong> Post is canonical; user/profile/privacy are canonical.
                      </>
                    ),
                    detail:
                      "These are updated with strong correctness requirements.",
                  },
                  {
                    main: (
                      <>
                        <strong>Read model:</strong> FeedEntry is derived (viewerId → ordered list of postIds + scores).
                      </>
                    ),
                    detail:
                      "This avoids expensive joins on every feed read; hydration is a batched lookup.",
                  },
                  {
                    main: (
                      <>
                        <strong>Amplification trade:</strong> we accept extra writes in the fan-out pipeline to keep read latency low.
                      </>
                    ),
                    detail:
                      `Posts ingest ~${fb.postIngestGbPerDay.toFixed(1)} GB/day (metadata only, rough). Feed projections can be much larger, so we use hybrid fan-out to control write amplification.`,
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* H */}
          <Section id="schema-evolution" title="H) Schema evolution & compatibility — events + DB migrations">
            <Say>“I evolve schemas safely with additive changes, versioning, and expand/contract migrations.”</Say>

            <Panel title="Events (Avro/Protobuf/JSON)" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Avro/Protobuf:</strong> typed, compact, supports compat rules.
                      </>
                    ),
                    detail:
                      "Add optional fields with defaults; never reuse field IDs (Proto); use a registry and CI validation.",
                  },
                  {
                    main: (
                      <>
                        <strong>JSON:</strong> flexible but needs discipline.
                      </>
                    ),
                    detail:
                      "Use versioned envelopes + JSON Schema; tolerant readers; never break old consumers.",
                  },
                ]}
              />
            </Panel>

            <Panel title="DB migrations (expand → migrate → contract)" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Expand:</strong> add nullable columns/tables/indexes first.
                      </>
                    ),
                    detail:
                      "Deploy readers/writers that understand both old and new shape.",
                  },
                  {
                    main: (
                      <>
                        <strong>Migrate/backfill:</strong> backfill data + dual-write if needed; validate.
                      </>
                    ),
                    detail:
                      "Use idempotent jobs; measure progress; keep rollback path.",
                  },
                  {
                    main: (
                      <>
                        <strong>Contract:</strong> remove old columns/paths only after traffic cutover.
                      </>
                    ),
                    detail:
                      "Canary + rollback to reduce risk.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — evolution examples" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Event evolution:</strong> <code>PostCreated</code> adds an optional <code>language</code> field (default “unknown”).
                      </>
                    ),
                    detail:
                      "Old consumers keep working; new consumers can use the field for ranking or filtering.",
                  },
                  {
                    main: (
                      <>
                        <strong>Feed model upgrade:</strong> add <code>rankScoreV2</code> while keeping <code>rankScore</code>.
                      </>
                    ),
                    detail:
                      "Backfill/compute V2 scores asynchronously; switch readers; then remove old score later (contract).",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* Mini case */}
          <Section id="case" title="Mini case: Facebook-like Home Feed — put it all together">
            <Say>“Here’s a cohesive storage design for Home Feed: canonical stores + derived views + caches + safe evolution.”</Say>

            <Panel title="One possible data-store layout" tag="Example">
              <MonoBlock>{`Core (canonical):
- User/Privacy/Blocks: SQL or strong document store (correctness)
- FollowStore: edges indexed for follower/following lookups
- PostStore: durable post metadata/text (media in Object Store + CDN)

Derived (read-optimized):
- FeedStore (viewerId partition): ordered FeedEntry list (postId + score + ts)
- Search index: posts/users full-text (async)

Caching:
- Redis: feed head pages + post summary hydration + rate limits

Consistency:
- Strong for privacy/block/security
- Eventual for feed projections/counters/ranking signals (bounded seconds)

Hotspots:
- Hybrid fan-out: normal authors fan-out on write
- Celebrity authors: fan-in merge at read time to avoid extreme write amplification`}</MonoBlock>
            </Panel>
          </Section>

          {/* Sound bites */}
          <Section id="soundbites" title="Quick sound bites you can use in the interview">
            <Panel title="Use these lines confidently" tag="Template">
              <BulletList
                items={[
                  { main: "“Pick the database after you write down access patterns and SLOs.”" },
                  { main: "“Indexes reduce reads but increase writes—keep OLTP indexes lean.”" },
                  { main: "“Shard by a stable key; use virtual nodes; plan for hotspots.”" },
                  { main: "“Strong consistency for security/privacy; eventual for feeds/counters with bounded staleness.”" },
                  { main: "“Expand → backfill/dual-write → cut over → contract.”" },
                ]}
              />
            </Panel>
          </Section>
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none order-xl-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
